// Доступ статьи — состояние модерации бэка (`ModerationState.visibility`), не поле формы:
// любое его изменение идёт через `PATCH /instructions/{id}/visibility`, и бэк сам решает,
// применить сразу или отправить статью на проверку. Правило отказов и текстов успеха —
// общее с курсом (`moderation/lib/visibilityChange.ts`).
import type { AccessType } from '@/features/instructions/types'
import { useSetArticleVisibilityMutation } from '@/features/moderation/articleModerationApi'
import { useArticleModeration } from '@/features/moderation/useArticleModeration'
import { fromVisibility } from '@/features/moderation/lib/visibility'
import { applyVisibility, type VisibilityTexts } from '@/features/moderation/lib/visibilityChange'
import { useRetryCountdown } from '@/features/editor/components/publish/useRetryCountdown'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

const texts = (): VisibilityTexts => {
  const m = messagesNow(messages)
  return {
    review: m.accessSentForReview,
    done: { private: m.accessPrivate, link: m.accessLink, public: m.accessPublic },
  }
}

/** `id === null` — статья ещё не создана: доступа нет, менять нечего. */
export const useArticleAccess = (id: number | null) => {
  const [setVisibility, { isLoading }] = useSetArticleVisibilityMutation()
  const { state, isError, refetch } = useArticleModeration(id)
  const countdown = useRetryCountdown()

  // Валидатора публикации у статьи нет: `onValidation` не передаётся — общий тост.
  const applyAccess = async (next: AccessType): Promise<void> => {
    if (id === null) return
    await applyVisibility({
      state,
      next,
      texts: texts(),
      request: () => setVisibility({ id, accessType: next }).unwrap(),
      refetch,
      startCountdown: countdown.start,
    })
  }

  return {
    moderation: state,
    // Упала ПЕРВАЯ загрузка: данных нет — вкладке нужны причина и «Повторить».
    moderationError: isError && state === undefined,
    reloadModeration: refetch,
    // «Не знаем» ≠ «закрыта»: без состояния модерации доступ undefined, а не 'private'.
    access: state ? fromVisibility(state.visibility) : undefined,
    applyAccess,
    isBusy: isLoading,
    retryLabel: countdown.label,
  }
}

export type ArticleAccess = ReturnType<typeof useArticleAccess>
